import MapboxDraw, { DrawCustomModeThis, DrawFeature, DrawPoint } from "@mapbox/mapbox-gl-draw";
import { Feature, Point } from "geojson";
import { defer } from "lodash";

import { DRAWING_CONSTANTS } from "fond/constants";
import { inPole } from "fond/layers";
import { LayerIds } from "fond/types";

import { pullInFeatures as pullInAerialFeatures } from "../topologyHelper";

import { pullInFeatures, refreshSnappedStatus } from "./helper";

const { constants: Constants } = MapboxDraw;

/**
 * Aerial config for checking collocated aerial poles at split points.
 */
const aerialConfig = { lineLayerId: LayerIds.inSpan, pointLayerId: inPole };

/**
 * onSplit callback for underground paths.
 *
 * Called by the lineSplit mode after an underground path is split.
 * Creates underground points at each split intersection point,
 * skipping locations where a point or pole already exists.
 */
export function onSplitUndergroundPath(
  this: DrawCustomModeThis,
  _originalFeature: DrawFeature,
  _newFeatures: DrawFeature[],
  splitPoints: Feature<Point>[]
): void {
  const newPoints: DrawPoint[] = [];

  splitPoints.forEach((splitPoint) => {
    const { coordinates } = splitPoint.geometry;

    const tempPoint = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: { layerId: LayerIds.inStreetPoints, [DRAWING_CONSTANTS.Unsnapped]: "false" },
      geometry: {
        type: Constants.geojsonTypes.POINT,
        coordinates,
      },
    }) as DrawPoint;

    // Check for existing underground points at this location
    const { points: existingUgPoints } = pullInFeatures.call(this, tempPoint);

    // Check for existing aerial poles at this location
    const { points: existingPoles } = pullInAerialFeatures.call(this, aerialConfig, tempPoint);

    if (existingUgPoints.length > 0 || existingPoles.length > 0) {
      // Point already exists at this location — don't create duplicate
      this.deleteFeature(tempPoint.id as string, { silent: true });
    } else {
      this.addFeature(tempPoint);
      newPoints.push(tempPoint);
      refreshSnappedStatus.call(this, tempPoint);
    }
  });

  if (newPoints.length > 0) {
    // Defer setting the id property to match the MapboxDraw-assigned id
    defer(() => {
      newPoints.forEach((p) => {
        p.setProperty("id", p.id);
      });
    });
  }
}
